import { GeoJSONFeature } from 'mapbox-gl'
import { action, computed, makeObservable, observable } from 'mobx'

import { IGeoJson2DGeographicCoordinates, ITileset } from '~/client/graph'

import { TILESET_SOURCE } from '../components/MapBoxViewer/mapboxConstants'
import { IShapePart } from '../components/SitemapHelpers/models/PolyLineShapeCoordinates'
import GlobeView from '../models/GlobeView'
import {
  getFeatureAnchor,
  getFeatureParts,
  IFeatureParts,
} from '../utils/SitemapCalculationHelpers'
import { isTilesetReady } from '../utils/tilesetReadiness'
import TilesetsStore from './domain/Tilesets.store'
import GlobeViewControlStore from './GlobeViewControl.store'
import InitialState from './InitialState'

export default class TilesetsControlStore {
  @observable public selectedTilesetFeatures: Array<GeoJSONFeature> = []
  @observable public selectedTilesetFeature: GeoJSONFeature = null
  @observable public selectedTilesetId: string = null

  public constructor(
    private readonly tilesetsStore: TilesetsStore,
    private readonly state: InitialState,
    public readonly globeViewControlStore: GlobeViewControlStore,
  ) {
    makeObservable(this)
  }

  public get selectedGlobe(): GlobeView {
    return this.globeViewControlStore.selectedGlobeView
  }

  // tilesets to show as additional on the 'GLOBE'. These ids also drive the
  // interactive tileset layers, so a non-ready tileset must not stay clickable
  // on a globe view whose Map Files list no longer offers it.
  @computed
  public get globeTilesets(): ITileset[] {
    const { activeTilesetsById } = this.state
    return (this.selectedGlobe?.tilesetIds || [])
      .map(tilesetId => activeTilesetsById.get(tilesetId))
      .filter(isTilesetReady)
  }

  public get selectedFeatureTileset(): ITileset {
    const index = TILESET_SOURCE.length
    const id = this.selectedTilesetFeature?.source?.slice(index)
    return this.globeTilesets?.find(t => t.id === id)
  }

  @computed
  private get selectedTilesetFeatureParts(): IFeatureParts | undefined {
    return getFeatureParts(this.selectedTilesetFeature?.geometry)
  }

  public get selectedTilesetFeaturePartsCount(): number {
    return this.selectedTilesetFeatureParts?.parts.length ?? 0
  }

  public get selectedTilesetFeatureIsClosed(): boolean {
    return !!this.selectedTilesetFeatureParts?.isClosed
  }

  public get selectedTilesetFeatureShapeParts(): IShapePart[] {
    return (
      this.selectedTilesetFeatureParts?.parts.map(rings => ({
        rings: rings.map(ring =>
          ring.map(([longitude, latitude]) => ({ longitude, latitude })),
        ),
      })) ?? []
    )
  }

  public get selectedTilesetFeatureCoordinatesCenter(): IGeoJson2DGeographicCoordinates {
    const [longitude, latitude] = getFeatureAnchor(
      this.selectedTilesetFeature.geometry,
    )
    return { longitude, latitude }
  }

  @action.bound
  public setSelectedTilesetFeature(feature: GeoJSONFeature): void {
    this.selectedTilesetFeature = feature
  }

  @action.bound
  public resetTilesetFeaturesSelection(): void {
    this.selectedTilesetFeature = null
    this.selectedTilesetFeatures = []
  }
}
